import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import adjustIcon from "../../assets/icons/adjust(1)1.svg";
import thinking1 from "../../assets/icons/thinking1.svg";
import fetchWrapper from "../../AuthMiddleware/FetchWrapper";
import { hasPermission } from "../../Services/authUtils";

const importIcon = async (iconName) => {
  try {
    const icon = await import(`../../assets/icons/${iconName}.svg`);
    return icon.default;
  } catch (error) {
    // console.error(`Failed to import icon: ${iconName}`, error);
    return adjustIcon; // Default icon in case of import failure
  }
};

const filterMenuItems = (items) => {
  return items
    .map((item) => {

      if (item.subItems) {
        const filteredSubItems = item.subItems.filter((subItem) => {
          const hasPerm = hasPermission(subItem.requiredPermissions || []);

          return hasPerm;
        });
        return {
          ...item,
          subItems: filteredSubItems,
        };
      }
      return item;
    })
    .filter((item) => {
      const hasRequiredPermissions =
        !item.requiredPermissions || hasPermission(item.requiredPermissions);

      return hasRequiredPermissions;
    });
};

const staticItems = [
  {
    title: "User Settings",
    icon: thinking1,
    subItems: [
      {
        title: "Role Groups",
        link: "/rolegroups",
        requiredPermissions: ["roleGroups.view"],
      },
      { title: "Roles", link: "/roles", requiredPermissions: ["roles.view"] },
      {
        title: "Department",
        link: "/department",
        requiredPermissions: ["departments.view"],
      },
      { title: "Users", link: "/users", requiredPermissions: ["users.view"] },
      {
        title: "Permissions",
        link: "/permission",
        requiredPermissions: ["permission.view"],
      },
    ],
  },
  {
    title: "Masters",
    icon: thinking1,
    link: "/master",
    requiredPermissions: ["masters.view"],
  },
];

export const fetchMenuItems = createAsyncThunk(
  "menu/fetchMenuItems",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchWrapper(
        `${process.env.REACT_APP_BASE_API_URL1}/api/v1/parameter-type/getall`,
        {
          method: "POST",
          body: JSON.stringify({}),
        }
      );

      const items = await Promise.all(
        data.map(async (item) => ({
          title: item.parameter_type_name,
          icon: await importIcon(item.icon),
          requiredPermissions: ["parameters.view"],
          subItems: item.masterDetails.map((detail) => ({
            title: detail.masterName,
            link: `/parameter_types/${item._id}/masters/${detail.masterId}`,
            requiredPermissions: ["parameters.view"], // Ensure each subitem has required permissions
          })),
        }))
      );

      const combinedItems = [...items, ...staticItems];
      const filteredItems = filterMenuItems(combinedItems);

      const totalMasterDetailsLength = data.reduce(
        (acc, item) => acc + item.masterDetails.length,
        0
      );

      return { items: filteredItems, totalMasterDetailsLength };
    } catch (error) {
      // If fetching data fails, filter static items
      const combinedItems = [...staticItems]; // Only use static items
      const filteredItems = filterMenuItems(combinedItems); // Filter static items based on permissions

      const totalMasterDetailsLength = 0; // Set to zero since we are not fetching any data

      // Return filtered static items
      return { items: filteredItems, totalMasterDetailsLength };
      // Optionally log the error if needed
      // console.error("Failed to fetch menu items", error);
    }
  }
);


const menuSlice = createSlice({
  name: "menu",
  initialState: {
    items: [],
    totalMasterDetailsLength: 0,
    status: "idle",
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMenuItems.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchMenuItems.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload.items;
        state.totalMasterDetailsLength =
          action.payload.totalMasterDetailsLength;
      })
      .addCase(fetchMenuItems.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export default menuSlice.reducer;
